import React, { Component } from "react";
import { Link } from "react-router-dom";
import API from "../utils/api";

class DashboardPage extends Component {
  state = {
    loading: true,
    stats: null,
    error: null,
    targetProgress: null,
  };

  componentDidMount() {
    this.fetchStats();
    this.fetchTargetProgress();
  }

  fetchStats = async () => {
    try {
      const res = await API.get("/dashboard");
      this.setState({ stats: res.data.data, loading: false });
    } catch (e) {
      this.setState({ error: "Failed to load dashboard", loading: false });
    }
  };

  fetchTargetProgress = async () => {
    try {
      const res = await API.get("/my-target-progress");
      const data = res.data.data || res.data;
      this.setState({ targetProgress: data });
    } catch (e) {
      console.error("Failed to load target progress", e);
    }
  };

  render() {
    const { loading, stats, error, targetProgress } = this.state;
    const tgt = targetProgress ? targetProgress.target : {};
    const prg = targetProgress ? targetProgress.progress : {};
    const today = prg.today || {};
    const postedPct = tgt.posted ? Math.min(100, Math.round(((today.posted || 0) / tgt.posted) * 100)) : 0;
    const attemptedPct = tgt.attempted ? Math.min(100, Math.round(((today.attempted || 0) / tgt.attempted) * 100)) : 0;
    const postedPending = Math.max(0, (tgt.posted || 0) - (today.posted || 0));
    const attemptedPending = Math.max(0, (tgt.attempted || 0) - (today.attempted || 0));
    const hasTarget = targetProgress && (tgt.posted > 0 || tgt.attempted > 0);

    if (loading) {
      return (
        <div>
          <h2 className="page-header">Dashboard</h2>
          <p>
            <i className="fas fa-spinner fa-spin"></i> Loading...
          </p>
        </div>
      );
    }

    if (error) {
      return (
        <div>
          <h2 className="page-header">Dashboard</h2>
          <div className="alert alert-danger">{error}</div>
        </div>
      );
    }

    return (
      <div>
        <h2 className="page-header">Dashboard</h2>

        {/* Daily Target Progress */}
        {hasTarget && (
          <div className="panel panel-success" style={{ marginBottom: 20 }}>
            <div className="panel-heading">
              <h4 style={{ margin: 0 }}><i className="fas fa-bullseye"></i> Your Daily Target</h4>
            </div>
            <div className="panel-body">
              <div className="row">
                {tgt.posted > 0 && (
                  <div className="col-sm-6">
                    <div style={{ marginBottom: 10 }}>
                      <strong>Posted:</strong>{" "}
                      <span style={{ fontSize: 22, fontWeight: "bold", color: postedPct >= 100 ? "#3c763d" : "#8a6d3b" }}>
                        {today.posted || 0}
                      </span>
                      <span style={{ color: "#999" }}> / {tgt.posted}</span>
                      {postedPct >= 100
                        ? <span className="label label-success" style={{ marginLeft: 8 }}><i className="fas fa-check"></i> Target Met!</span>
                        : <span className="label label-warning" style={{ marginLeft: 8 }}>{postedPending} pending</span>
                      }
                    </div>
                    <div className="progress" style={{ height: 22, marginBottom: 5 }}>
                      <div className={`progress-bar ${postedPct >= 100 ? "progress-bar-success" : postedPct >= 50 ? "progress-bar-info" : "progress-bar-warning"}`}
                        role="progressbar"
                        style={{ width: postedPct + "%", minWidth: postedPct > 0 ? "2em" : 0 }}>
                        {postedPct}%
                      </div>
                    </div>
                  </div>
                )}
                {tgt.attempted > 0 && (
                  <div className="col-sm-6">
                    <div style={{ marginBottom: 10 }}>
                      <strong>Attempted:</strong>{" "}
                      <span style={{ fontSize: 22, fontWeight: "bold", color: attemptedPct >= 100 ? "#3c763d" : "#8a6d3b" }}>
                        {today.attempted || 0}
                      </span>
                      <span style={{ color: "#999" }}> / {tgt.attempted}</span>
                      {attemptedPct >= 100
                        ? <span className="label label-success" style={{ marginLeft: 8 }}><i className="fas fa-check"></i> Target Met!</span>
                        : <span className="label label-warning" style={{ marginLeft: 8 }}>{attemptedPending} pending</span>
                      }
                    </div>
                    <div className="progress" style={{ height: 22, marginBottom: 5 }}>
                      <div className={`progress-bar ${attemptedPct >= 100 ? "progress-bar-success" : attemptedPct >= 50 ? "progress-bar-info" : "progress-bar-warning"}`}
                        role="progressbar"
                        style={{ width: attemptedPct + "%", minWidth: attemptedPct > 0 ? "2em" : 0 }}>
                        {attemptedPct}%
                      </div>
                    </div>
                  </div>
                )}
              </div>
              {/* Period summary - Posted & Attempted */}
              {prg.yesterday && (
                <div style={{ marginTop: 15, borderTop: "1px solid #ddd", paddingTop: 10 }}>
                  <div className="row">
                    {[
                      { label: "Yesterday", posted: prg.yesterday.posted, attempted: prg.yesterday.attempted, color: "#31708f" },
                      { label: "This Week", posted: prg.thisWeek.posted, attempted: prg.thisWeek.attempted, color: "#3c763d" },
                      { label: "This Month", posted: prg.thisMonth.posted, attempted: prg.thisMonth.attempted, color: "#8a6d3b" },
                      { label: "Last Month", posted: prg.lastMonth.posted, attempted: prg.lastMonth.attempted, color: "#a94442" },
                    ].map((item) => (
                      <div className="col-xs-6 col-sm-3 text-center" key={item.label} style={{ marginBottom: 8 }}>
                        <div style={{ fontSize: 18, fontWeight: "bold", color: item.color }}>{item.posted || 0}</div>
                        <small className="text-muted">Posted</small>
                        <div style={{ fontSize: 16, fontWeight: "bold", color: "#666" }}>{item.attempted || 0}</div>
                        <small className="text-muted">Attempted</small>
                        <div style={{ marginTop: 2, fontSize: 11, color: "#999" }}>{item.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Stat Cards */}
        <div className="row">
          <div className="col-md-3">
            <div className="stat-card">
              <h3>{stats.totalLeads}</h3>
              <p>Total Leads</p>
            </div>
          </div>
          <div className="col-md-3">
            <div className="stat-card green">
              <h3>{stats.verifiedLeads}</h3>
              <p>Verified Leads</p>
            </div>
          </div>
          <div className="col-md-3">
            <div className="stat-card yellow">
              <h3>{stats.todayLeads}</h3>
              <p>Today's Leads</p>
            </div>
          </div>
          <div className="col-md-3">
            <div className="stat-card red">
              <h3>{stats.monthLeads}</h3>
              <p>This Month</p>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="row" style={{ marginBottom: 20 }}>
          <div className="col-md-12">
            <Link to="/leads/add" className="btn btn-success">
              <i className="fas fa-plus"></i> Add New Lead
            </Link>{" "}
            <Link to="/leads" className="btn btn-default">
              <i className="fas fa-list"></i> View All Leads
            </Link>
          </div>
        </div>

        {/* Breakdowns */}
        <div className="row">
          <div className="col-md-4">
            <div className="breakdown-section">
              <h4>
                <i className="fas fa-pills"></i> Medicine Type
              </h4>
              {stats.medicineTypeBreakdown && stats.medicineTypeBreakdown.length > 0 ? (
                stats.medicineTypeBreakdown.map((item, i) => (
                  <div className="breakdown-item" key={i}>
                    <span>{item.medicine_type}</span>
                    <span className="badge">{item.count}</span>
                  </div>
                ))
              ) : (
                <p className="text-muted">No data</p>
              )}
            </div>
          </div>
          <div className="col-md-4">
            <div className="breakdown-section">
              <h4>
                <i className="fas fa-concierge-bell"></i> Service For
              </h4>
              {stats.serviceForBreakdown && stats.serviceForBreakdown.length > 0 ? (
                stats.serviceForBreakdown.map((item, i) => (
                  <div className="breakdown-item" key={i}>
                    <span>{item.service_for}</span>
                    <span className="badge">{item.count}</span>
                  </div>
                ))
              ) : (
                <p className="text-muted">No data</p>
              )}
            </div>
          </div>
          <div className="col-md-4">
            <div className="breakdown-section">
              <h4>
                <i className="fas fa-map-marker-alt"></i> PCD Location
              </h4>
              {stats.pcdLocationBreakdown && stats.pcdLocationBreakdown.length > 0 ? (
                stats.pcdLocationBreakdown.map((item, i) => (
                  <div className="breakdown-item" key={i}>
                    <span>{item.pcd_area}</span>
                    <span className="badge">{item.count}</span>
                  </div>
                ))
              ) : (
                <p className="text-muted">No data</p>
              )}
            </div>
          </div>
        </div>

        {/* Service Type Breakdown */}
        <div className="row">
          <div className="col-md-4">
            <div className="breakdown-section">
              <h4>
                <i className="fas fa-briefcase"></i> Service Type
              </h4>
              {stats.serviceTypeBreakdown && stats.serviceTypeBreakdown.length > 0 ? (
                stats.serviceTypeBreakdown.map((item, i) => (
                  <div className="breakdown-item" key={i}>
                    <span>{item.service_type}</span>
                    <span className="badge">{item.count}</span>
                  </div>
                ))
              ) : (
                <p className="text-muted">No data</p>
              )}
            </div>
          </div>
        </div>

        {/* Recent Leads */}
        <div className="row">
          <div className="col-md-12">
            <div className="breakdown-section">
              <h4>
                <i className="fas fa-clock"></i> Recent Leads
              </h4>
              {stats.recentLeads && stats.recentLeads.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-striped table-condensed">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Mobile</th>
                        <th>Email</th>
                        <th>Service Type</th>
                        <th>Source</th>
                        <th>Status</th>
                        <th>Posted On</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.recentLeads.map((lead) => (
                        <tr key={lead.id}>
                          <td>{lead.name}</td>
                          <td>{lead.mobile}</td>
                          <td>{lead.email}</td>
                          <td>{lead.service_type}</td>
                          <td>{lead.source}</td>
                          <td>
                            <StatusBadge status={lead.status} />
                          </td>
                          <td>
                            {lead.posted_on
                              ? new Date(lead.posted_on).toLocaleDateString()
                              : "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-muted">No recent leads</p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const StatusBadge = ({ status }) => {
  const map = {
    2: { label: "Spam", cls: "label-danger" },
    5: { label: "New-Incomplete", cls: "label-warning" },
    6: { label: "New-Complete", cls: "label-success" },
    7: { label: "Old-Incomplete", cls: "label-warning" },
    8: { label: "Old-Complete", cls: "label-info" },
    9: { label: "From Tele", cls: "label-primary" },
  };
  const info = map[status] || { label: status, cls: "label-default" };
  return <span className={`label ${info.cls}`}>{info.label}</span>;
};

export default DashboardPage;
